// Conversation lifecycle: queue (max 4 concurrent LLM gens), playback
// (typewriter + linger + click-to-skip), end → delta → cooldown.
// Pure playback stepper is exported for tests; React shell below.
import { useCallback, useEffect, useRef, useState } from 'react';
import { TUNING, CONVO_COLORS } from '../constants.js';
import { chatComplete, deepSeekReady, LlmTransportError } from '../llm/client.js';
import { offlineChatLines, parseDialogue, isUsableScript, randomLengthClass } from '../llm/dialogue.js';
import { buildDeltaPrompt, buildDialoguePrompt, DeltaSchema, parseJsonLoose } from '../llm/prompts.js';
import { lingerMsForLine } from '../sim/bubbles.js';
import { rollMs, talkRangeMs } from '../sim/conversationPace.js';
import { createOpeningHistory, openingBans, recordOpening } from '../sim/openingHistory.js';
import { SCENARIOS, type Scenario } from '../sim/scenarios.js';
import type { Conversation, DialogueLine, LengthClass, Memory, Npc, Relationship, SleepState } from '../types.js';
import { uid } from './spawn.js';
import type { WorldLaunchHandoff } from './CreateWorld/draftStorage.js';

export interface ActiveConvo extends Conversation {
  revealed: number; // chars of current line shown
  lingerUntil: number | null; // sim-ms when current line expires
  fade?: { speaker: string; text: string; until: number } | null;
  /** Sim-ms when this conversation ends even if lines remain. Absent = play the script out. */
  until?: number;
}

interface Ctx {
  npcById: (id: string) => Npc;
  relOf: (a: string, b: string) => Relationship | null;
  memsOf: (a: string, b: string) => Memory[];
  longOf: (a: string, b: string) => string;
  worldHandoff: WorldLaunchHandoff | null;
  // Sleep depth at generation time — read BEFORE onPaired wakes them, so a
  // woken napper still gets groggy lines.
  sleepOf: (id: string) => SleepState;
}

function convoPairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

// Playback step (pure): advance revealed chars; returns finished-line events.
export function stepPlayback(
  c: ActiveConvo,
  dtMs: number,
  now: number,
  lingerMult: number = TUNING.lingerMultiplier,
): { next: ActiveConvo; lineDone: boolean; convoDone: boolean } {
  if (c.until != null && now >= c.until) {
    return { next: c, lineDone: false, convoDone: true };
  }
  const fade = c.fade && now >= c.fade.until ? null : c.fade;
  const line = c.lines[c.cursor];
  if (!line) {
    if (c.until != null && now < c.until) {
      return { next: { ...c, fade }, lineDone: false, convoDone: false };
    }
    if (fade && now < fade.until) return { next: { ...c, fade }, lineDone: false, convoDone: false };
    return { next: { ...c, fade: null }, lineDone: false, convoDone: true };
  }
  const full = line.text.length;
  const revealed = Math.min(full, c.revealed + (dtMs / TUNING.typewriterMsPerChar));
  if (revealed < full) {
    return { next: { ...c, revealed, lingerUntil: null, fade }, lineDone: false, convoDone: false };
  }
  const until = c.lingerUntil ?? now + lingerMsForLine(line.text, lingerMult);
  if (now >= until) {
    const cursor = c.cursor + 1;
    const nextFade = { speaker: line.speaker, text: line.text, until: now + TUNING.bubbleFadeMs };
    return { next: { ...c, cursor, revealed: 0, lingerUntil: null, fade: nextFade }, lineDone: true, convoDone: false };
  }
  return { next: { ...c, revealed: full, lingerUntil: until, fade }, lineDone: false, convoDone: false };
}

export function useConversations(
  ctx: Ctx,
  opts: {
    onPaired: (a: string, b: string, convoId: string) => void;
    onEnded: (convo: ActiveConvo) => void;
    genDialogue?: (a: Npc, b: Npc, cls: LengthClass) => Promise<string>;
    /** Live in-world ISO instant for dialogue prompts (not browser local time). */
    worldNowIso?: () => string | undefined;
  },
  lingerMult: number = TUNING.lingerMultiplier,
  scenario: Scenario = SCENARIOS[0],
): {
  active: ActiveConvo[];
  pending: number;
  generating: number;
  llmDown: number; // consecutive TRANSPORT failures — caller shows offline pill at >=3.
  // Parse failures (200 OK but unusable script) deliberately do NOT move this:
  // the LLM is reachable, our strict parser just rejected its style.
  request: (a: string, b: string) => void;
  tick: (dtMs: number, now: number) => void;
  skipLine: (id: string) => void;
  dropFor: (npcId: string) => void;
} {
  const [active, setActive] = useState<ActiveConvo[]>([]);
  const [pending, setPending] = useState<Array<[string, string]>>([]);
  const [generating, setGenerating] = useState(0);
  const [llmDown, setLlmDown] = useState(0);
  // Linger setting via ref so the playback tick doesn't resubscribe.
  const lingerRef = useRef(lingerMult);
  lingerRef.current = lingerMult;
  const colorIdx = useRef(0);
  const openings = useRef(createOpeningHistory());
  const simNowRef = useRef(0);
  const inFlightRef = useRef(new Set<string>());
  const activeRef = useRef<ActiveConvo[]>([]);
  activeRef.current = active;
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const request = useCallback((a: string, b: string) => {
    const key = convoPairKey(a, b);
    if (inFlightRef.current.has(key)) return;
    if (activeRef.current.some((c) => convoPairKey(c.aId, c.bId) === key)) return;
    setPending((q) => (q.some(([x, y]) => (x === a && y === b) || (x === b && y === a)) ? q : [...q, [a, b]]));
  }, []);

  // Pump queue with concurrency cap.
  useEffect(() => {
    if (generating >= TUNING.maxConcurrentDialogueGen || pending.length === 0) return;
    const [[aId, bId], ...rest] = pending;
    const pairKeyNow = convoPairKey(aId, bId);
    if (inFlightRef.current.has(pairKeyNow)) {
      setPending(rest);
      return;
    }
    inFlightRef.current.add(pairKeyNow);
    setPending(rest);
    setGenerating((g) => g + 1);
    void (async () => {
      const c = ctxRef.current;
      const a = c.npcById(aId);
      const b = c.npcById(bId);
      const cls = randomLengthClass();
      const aName = `${a.firstName} ${a.lastName}`;
      const bName = `${b.firstName} ${b.lastName}`;
      const failParse = (): void => {
        optsRef.current.onEnded({
          id: 'failed',
          aId,
          bId,
          lengthClass: cls,
          lines: [],
          color: '',
          cursor: 0,
          revealed: 0,
          lingerUntil: null,
        });
      };
      const show = (lines: DialogueLine[], record: boolean): void => {
        if (record && lines[0]) recordOpening(openings.current, aId, bId, lines[0]);
        const convo: ActiveConvo = {
          id: uid('convo'), aId, bId, lengthClass: cls,
          lines, color: CONVO_COLORS[colorIdx.current++ % CONVO_COLORS.length],
          cursor: 0, revealed: 0, lingerUntil: null,
          until: simNowRef.current + rollMs(talkRangeMs),
        };
        const showKey = convoPairKey(aId, bId);
        setActive((list) => (list.some((c) => convoPairKey(c.aId, c.bId) === showKey) ? list : [...list, convo]));
        optsRef.current.onPaired(aId, bId, convo.id);
      };
      try {
        let raw: string;
        try {
          if (optsRef.current.genDialogue) {
            raw = await optsRef.current.genDialogue(a, b, cls);
          } else {
            const prompt = buildDialoguePrompt(
              a, b,
              c.relOf(aId, bId), c.relOf(bId, aId),
              c.memsOf(aId, bId), c.memsOf(bId, aId),
              c.longOf(aId, bId), c.longOf(bId, aId),
              cls,
              c.sleepOf(aId), c.sleepOf(bId),
              scenario,
              c.worldHandoff,
              openingBans(openings.current, aId, bId),
              optsRef.current.worldNowIso?.(),
            );
            raw = await chatComplete([{ role: 'user', content: prompt }], {
              maxTokens: cls === 'long' ? 4000 : 2000,
              temperature: 1.0,
            });
          }
        } catch (e) {
          // No key: dev still shows a conversation. A configured key never substitutes a script.
          if (!deepSeekReady()) {
            show(offlineChatLines(aName, bName, a.firstName, b.firstName), false);
            return;
          }
          if (e instanceof LlmTransportError) setLlmDown((s) => s + 1);
          failParse();
          return;
        }
        setLlmDown(0);
        const parsed = parseDialogue(raw, aName, bName);
        if (!isUsableScript(parsed)) {
          failParse();
          return;
        }
        show(parsed.lines, true);
      } finally {
        inFlightRef.current.delete(pairKeyNow);
        setGenerating((g) => g - 1);
      }
    })();
  }, [pending, generating]);

  const tick = useCallback((dtMs: number, now: number) => {
    simNowRef.current = now;
    const mult = lingerRef.current;
    setActive((list) => {
      const next: ActiveConvo[] = [];
      const done: ActiveConvo[] = [];
      for (const c of list) {
        const r = stepPlayback(c, dtMs, now, mult);
        if (r.convoDone) done.push(r.next);
        else next.push(r.next);
      }
      if (done.length > 0) {
        queueMicrotask(() => done.forEach((d) => optsRef.current.onEnded(d)));
      }
      return done.length > 0 ? next : list.some((c, i) => c !== next[i]) ? next : list;
    });
  }, []);

  const skipLine = useCallback((id: string) => {
    setActive((list) => {
      const next: ActiveConvo[] = [];
      for (const c of list) {
        if (c.id !== id) {
          next.push(c);
          continue;
        }
        const line = c.lines[c.cursor];
        if (!line) {
          next.push(c);
          continue;
        }
        if (c.revealed < line.text.length) {
          next.push({ ...c, revealed: line.text.length });
          continue;
        }
        const cursor = c.cursor + 1;
        if (cursor >= c.lines.length) {
          // Drop it here so the playback tick cannot end the same conversation again.
          queueMicrotask(() => optsRef.current.onEnded(c));
          continue;
        }
        next.push({ ...c, cursor, revealed: 0, lingerUntil: null });
      }
      return next;
    });
  }, []);

  // Remove convos involving a deleted NPC.
  const dropFor = useCallback((npcId: string) => {
    setActive((list) => list.filter((c) => c.aId !== npcId && c.bId !== npcId));
    setPending((q) => q.filter(([a, b]) => a !== npcId && b !== npcId));
  }, []);

  return { active, pending: pending.length, generating, llmDown, request, tick, skipLine, dropFor };
}

// Fire-and-forget delta call after a conversation ends (caller applies result).
export async function fetchDelta(
  a: Npc,
  b: Npc,
  lines: DialogueLine[],
): Promise<unknown> {
  const transcript = lines.map((l) => `${l.speaker}: ${l.text}`).join('\n');
  const text = await chatComplete(
    [{ role: 'user', content: buildDeltaPrompt(`${a.firstName} ${a.lastName}`, `${b.firstName} ${b.lastName}`, transcript) }],
    { json: true, maxTokens: 600, temperature: 0.3 },
  );
  return DeltaSchema.parse(parseJsonLoose(text));
}
